import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import api from '../api/axios';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background: rgba(30, 30, 30, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 700px;
  max-height: 80vh;
  overflow-y: auto;
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 20px;
  line-height: 1.6;
  font-size: 14px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 300px;
  padding: 15px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  font-size: 14px;
  font-family: 'Courier New', monospace;
  resize: vertical;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  justify-content: flex-end;
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }
  
  &:disabled {
    background: #555;
    color: #888;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SecondaryButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.5);
    color: #ffffff;
  }
  
  &.danger {
    background: rgba(239, 68, 68, 0.2);
    border-color: rgba(239, 68, 68, 0.4);
    color: #ef4444;
    
    &:hover {
      background: rgba(239, 68, 68, 0.3);
      border-color: rgba(239, 68, 68, 0.6);
    }
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const SuccessMessage = styled.div`
  background: rgba(74, 222, 128, 0.2);
  border: 1px solid rgba(74, 222, 128, 0.4);
  color: #4ade80;
  padding: 12px;
  border-radius: 8px;
  margin-top: 16px;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid rgba(239, 68, 68, 0.4);
  color: #ef4444;
  padding: 12px;
  border-radius: 8px;
  margin-top: 16px;
  text-align: center;
`;

const SkillsModal = ({ isOpen, onClose }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('success');

  // 组件打开时加载已有的 skills
  useEffect(() => {
    if (isOpen) {
      loadSkills();
    }
  }, [isOpen]);

  const loadSkills = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const response = await api.get('/skills');

      if (response.data.success && response.data.skill) {
        setContent(response.data.skill.content);
      } else {
        setContent('');
      }
    } catch (error) {
      console.error('加载 Skills 失败:', error);
      setContent('');
    }
  };

  const handleSave = async () => {
    if (!content.trim()) {
      setMessage('请输入有效的 Skills 内容');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await api.post('/skills/upload', {
        content: content
      });

      if (response.data.success) {
        setMessage('Skills 保存成功！');
        setMessageType('success');

        // 2秒后自动关闭
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('保存 Skills 失败:', error);
      setMessage(error.response?.data?.error || '保存失败，请稍后重试');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('确定要删除当前 Skills 吗？')) {
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await api.delete('/skills');

      if (response.data.success) {
        setContent('');
        setMessage('Skills 删除成功！');
        setMessageType('success');
      }
    } catch (error) {
      console.error('删除 Skills 失败:', error);
      setMessage(error.response?.data?.error || '删除失败，请稍后重试');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    if (file.type !== 'text/markdown' && !file.name.endsWith('.md')) {
      setMessage('请上传 .md 格式的文件');
      setMessageType('error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setContent(event.target.result);
      setMessage('文件读取成功');
      setMessageType('success');
    };
    reader.onerror = () => {
      setMessage('文件读取失败');
      setMessageType('error');
    };
    reader.readAsText(file);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Skills 配置</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <Description>
          Skills 是用于定制单词查询结果的配置文件。上传后，查询单词时会根据您配置的内容生成额外的学习材料。
          例如：针对输入的单词，生成3句对话，对话中必须包含该单词。
        </Description>

        <TextArea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="请输入您的 Skills 配置内容...

示例：
针对输入的单词，生成3句对话，对话中必须包含该单词。将对话英文原文和翻译输出。"
        />

        {message && (
          messageType === 'success' ? (
            <SuccessMessage>{message}</SuccessMessage>
          ) : (
            <ErrorMessage>{message}</ErrorMessage>
          )
        )}

        <ButtonContainer>
          <input
            type="file"
            id="file-upload"
            accept=".md,text/markdown"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <SecondaryButton onClick={() => document.getElementById('file-upload').click()}>
            上传文件
          </SecondaryButton>
          {content && (
            <SecondaryButton className="danger" onClick={handleDelete} disabled={loading}>
              删除
            </SecondaryButton>
          )}
          <SecondaryButton onClick={onClose}>
            取消
          </SecondaryButton>
          <PrimaryButton onClick={handleSave} disabled={loading}>
            {loading && <LoadingSpinner />}
            保存
          </PrimaryButton>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default SkillsModal;
